import React from "react";
import ReactMarkdown from "react-markdown";
import {
    ChatContentQuotes,
    ContradictionPair,
    ContradictionSeverity,
    ContradictionSeverityLabels,
    ContradictionSide,
} from "../../../routines/schemas";
import {ChatMessageContent} from "../../../schemas/chat_message";
import {useEnhancedDocumentsContext} from "../../../contexts/doc_contexts";
import {useDocumentSpotlight} from "../../../contexts/document_spotlight_context";

type ChatContentRendererProps = {
    content: ChatMessageContent;
};

const SEVERITY_STYLES: Record<ContradictionSeverity, string> = {
    [ContradictionSeverity.DIRECT]: "bg-rose-100 text-rose-800 border-rose-300",
    [ContradictionSeverity.PARTIAL]: "bg-amber-100 text-amber-800 border-amber-300",
    [ContradictionSeverity.IMPLIED]: "bg-slate-100 text-slate-700 border-slate-300",
};

const ChatContentRenderer: React.FC<ChatContentRendererProps> = ({content}) => {
    const {getDocument, setDocument} = useEnhancedDocumentsContext()!;
    const spotlightCtx = useDocumentSpotlight();

    const handleViewSource = async (verbatimQuote: string, sourceId: string, sourceLabel = 'Chat Quote', pageHint?: number) => {
        const doc = await getDocument(sourceId);
        setDocument(doc);
        // Delay spotlight jump so the document-change effect (clearAllSpotlights)
        // runs first. The jumpRequest persists through clearAllSpotlights and the
        // viewer picks it up once pdfReady/text is mounted.
        setTimeout(() => {
            spotlightCtx?.jumpToSpotlight({text: verbatimQuote, source: sourceLabel, pageHint});
        }, 50);
    };

    const renderContradictionSide = (side: ContradictionSide) => {
        const clickable = !!(side.doc_id && side.quote);
        return (
            <div
                className={`flex-1 rounded border border-gray-200 bg-white px-3 py-2 ${
                    clickable ? "cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition" : ""
                }`}
                onClick={() => {
                    if (clickable && side.doc_id) {
                        void handleViewSource(side.quote, side.doc_id, "Contradiction", side.page_start ?? undefined);
                    }
                }}
            >
                <div className="text-xs font-semibold text-gray-700">{side.speaker}</div>
                <p className="mt-1 text-sm italic text-gray-800">&ldquo;{side.quote}&rdquo;</p>
                {(side.doc_title || side.page_start != null) && (
                    <div className="mt-1 text-xs text-gray-500">
                        {side.doc_title || "Source document"}
                        {side.page_start != null ? ` \u00b7 ${side.line_start != null ? `p. ${side.page_start}:${side.line_start}${side.line_end && side.line_end !== side.line_start ? '\u2013' + side.line_end : ''}` : `p. ${side.page_start}`}` : ""}
                    </div>
                )}
            </div>
        );
    };

    const contradictions = (content.metadata?.contradictions as ContradictionPair[] | undefined) ?? [];

    const contradictionsBlock = contradictions.length > 0 && (
        <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Contradictions found
            </div>
            {contradictions.map((pair, idx) => (
                <div key={idx} className="rounded-md border border-gray-300 bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{pair.topic}</span>
                        <span
                            className={`px-2 py-0.5 text-xs rounded-full border ${
                                SEVERITY_STYLES[pair.severity] || SEVERITY_STYLES[ContradictionSeverity.IMPLIED]
                            }`}
                        >
                            {ContradictionSeverityLabels[pair.severity] || pair.severity}
                        </span>
                    </div>
                    <p className="text-sm text-gray-800">{pair.explanation}</p>
                    <div className="flex flex-col md:flex-row gap-2">
                        {renderContradictionSide(pair.left)}
                        {renderContradictionSide(pair.right)}
                    </div>
                </div>
            ))}
        </div>
    );

    // If we have structured content_chunks render them .
    if (content.content_chunks && content.content_chunks.length > 0) {
        return (
            <div className="space-y-3">
                {contradictionsBlock}
                {content.content_chunks.map((chunk: ChatContentQuotes, idx: number) => {
                    const {verbatim_quote, assistant_comment, chat_context_source_id} = chunk;

                    const isDocLink = !verbatim_quote && !!chat_context_source_id;

                    return (
                        <div key={idx} className="flex flex-col space-y-1">
                            {assistant_comment && (
                                <div
                                    className={`prose prose-sm max-w-none text-gray-900${
                                        isDocLink ? " cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition" : ""
                                    }`}
                                    onClick={isDocLink ? () => handleViewSource("", chat_context_source_id!) : undefined}
                                >
                                    <ReactMarkdown>{assistant_comment}</ReactMarkdown>
                                    {isDocLink && (
                                        <a className="text-xs text-indigo-600 underline">
                                            Open document
                                        </a>
                                    )}
                                </div>
                            )}

                            {verbatim_quote && (
                                <div
                                    className="border-l-4 border-indigo-400 bg-indigo-50 px-3 py-2 rounded-r cursor-pointer hover:bg-indigo-100 transition"
                                    onClick={() => chat_context_source_id && handleViewSource(verbatim_quote, chat_context_source_id)}>
                                    <p className="text-sm italic text-gray-800">&ldquo;{verbatim_quote}&rdquo;</p>
                                    {chat_context_source_id && <a className="text-xs text-indigo-600 underline mt-1 block">
                                        View source
                                    </a>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    if (contradictionsBlock) {
        return <div className="space-y-3">{contradictionsBlock}</div>;
    }

    // Fallback: render plain text
    if (content.text) {
        return (
            <div className="prose prose-sm max-w-none text-gray-900">
                <ReactMarkdown>{content.text}</ReactMarkdown>
            </div>
        );
    }

    return null;
};

export default ChatContentRenderer;
